import {
  DataEditorProps,
  DataEditorRef,
  GridCell,
  GridCellKind,
  GridColumn,
  GridMouseEventArgs,
  Item as GridItem,
  Rectangle
} from "@glideapps/glide-data-grid";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {useAsyncDataSource, useColumnSort} from "@glideapps/glide-data-grid-source";
import {RowCallback, RowEditedCallback} from "@glideapps/glide-data-grid-source/dist/ts/use-async-data-source";
import {SizedGridColumn} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {useDataGridColumnSort} from "./useDataGridColumnSort";
import type {ColumnSort} from "@ots-frontend/ots-ui-tw";
// eslint-disable-next-line @nx/enforce-module-boundaries -- Shared synchronous scalar sorting foundation.
import {getColumnSortedIndices} from "@ots-frontend/ots-ui-tw";
import type {DataGridContentColumn} from "@ots-frontend/ots-ui-tw";

export type GridColumnWithContent<T> = DataGridContentColumn<T>;

export function getGridColumn<T>(columnWithContent: GridColumnWithContent<T>): GridColumn {
  const {getContent, getSortValue, sortable, ...rest} = columnWithContent;
  return rest;
}

/**
 * @deprecated use {@link GridRowContextMenu}
 */
export interface GridCellContextMenu<T> {
  bounds: Rectangle
  col: number
  row: number
  data: T
  possibleHeight?: number
  onItemClick?: () => void
}

export interface GridRowContextMenu<T> {
  bounds: Rectangle
  col: number
  row: number
  data: T[]
  possibleHeight?: number
  onItemClick?: () => void
}

export interface GridContextMenu<T> {
  bounds: Rectangle
  rows: number[]
  data: T[]
}

export type GridSort = {
  col: number
  direction: "asc" | "desc"
}

export const isSizedGridColumn = (value: GridColumn): value is SizedGridColumn => {
  return "width" in value
};

export class ContentCache {
  // column -> row -> value
  private cachedContent: Map<number, Map<number, GridCell>> = new Map();

  get(col: number, row: number) {
    const colCache = this.cachedContent.get(col);

    if (colCache === undefined) {
      return undefined;
    }

    return colCache.get(row);
  }

  set(col: number, row: number, value: GridCell) {
    if (this.cachedContent.get(col) === undefined) {
      this.cachedContent.set(col, new Map());
    }

    const rowCache = this.cachedContent.get(col) as Map<number, GridCell>;
    rowCache.set(row, value);
  }
}

export type UseDataGridProps<T> = {
  colsMap: GridColumnWithContent<T>[],
  data: T[],
  sort?: GridSort,
  /** Opt-in controlled sort by stable ID, used by the shared persisted layout. */
  columnSort?: {value: ColumnSort | undefined; onChange: (sort: ColumnSort | undefined) => void},
  /** Supply this when column widths are managed by a persisted layout. */
  onColumnResize?: DataEditorProps["onColumnResize"],
  /** Suspend sorting when synthetic separator rows must stay attached to their records. */
  disableSorting?: boolean
}

export type UseDataGridResult<T> =
  Pick<DataEditorProps, "onColumnResize" | "onItemHovered" | "columns" | "onHeaderMenuClick" | "rows">
  &
  ReturnType<typeof useColumnSort>
  & {
  // extend props here
  hoverRow?: number
  sort?: GridSort
}

export function useDataGrid<T>(props: UseDataGridProps<T>): UseDataGridResult<T> {
  const {colsMap, data, sort, columnSort, onColumnResize: resizeColumn, disableSorting = false} = props;

  const [_hoverRow, _setHoverRow] = useState<number | undefined>(undefined);
  const columnKey = (column: GridColumn) => column.id ?? column.title;
  const [localSort, _setSort] = useState<ColumnSort | undefined>(() => sort && colsMap[sort.col]
    ? {id: columnKey(colsMap[sort.col]), direction: sort.direction} : undefined);
  const _sort = columnSort ? columnSort.value : localSort;
  const [widths, setWidths] = useState<Record<string, number>>({});
  const _colsMap = useMemo(() => resizeColumn ? colsMap : colsMap.map(column => {
    const width = widths[columnKey(column)];
    return width === undefined ? column : {...column, width};
  }), [colsMap, widths, resizeColumn]);

  const onColumnResize = useCallback<NonNullable<DataEditorProps["onColumnResize"]>>((column, newSize, index, newSizeWithGrow) => {
    if (resizeColumn) resizeColumn(column, newSize, index, newSizeWithGrow);
    else setWidths(previous => ({...previous, [columnKey(column)]: newSize}));
  }, [resizeColumn]);

  const cols = useMemo(() => {
    return _colsMap.map(getGridColumn);
  }, [_colsMap]);

  // Track sorting by identity so hiding or moving a column cannot sort a different field.
  const sortIndex = _sort ? cols.findIndex(column => columnKey(column) === _sort.id) : -1;
  const sortDirection = _sort?.direction;
  const sortable = sortIndex >= 0 && _colsMap[sortIndex]?.sortable !== false;
  const activeSort = useMemo<GridSort | undefined>(() => !disableSorting && sortable && sortDirection && sortIndex >= 0
    ? {col: sortIndex, direction: sortDirection} : undefined, [sortDirection, sortIndex, disableSorting, sortable]);

  const colsMapRef = useRef(_colsMap);
  colsMapRef.current = _colsMap;

  const getCellContent = useCallback(
    ([col, row]: GridItem): GridCell => {
      const column = colsMapRef.current[col];
      const value = data[row];

      // Filtering can remove rows while the grid still requests their cells.
      if (column === undefined || value === undefined) {
        return {
          kind: GridCellKind.Text,
          data: "",
          displayData: "",
          allowOverlay: false,
        };
      }

      return column.getContent(value, col);
    },
    [data]
  );

  const sortValue = activeSort ? _colsMap[activeSort.col]?.getSortValue : undefined;
  const rawOrder = useMemo(() => activeSort && sortValue ? getColumnSortedIndices(data, sortValue, activeSort.direction) : undefined,
    [data, sortValue, activeSort]);
  const sortArgs = useColumnSort({
    columns: cols,
    getCellContent: getCellContent,
    rows: data.length,
    sort: activeSort === undefined || sortValue ? undefined : {
      column: cols[activeSort.col],
      direction: activeSort.direction,
      mode: "smart"
    }
  });
  const {getCellContent: getNativeCellContent, getOriginalIndex: getNativeIndex} = sortArgs;
  const sortedCellContent = useCallback<typeof getCellContent>(([col, row]) => rawOrder
    ? getCellContent([col, rawOrder[row] ?? -1]) : getNativeCellContent([col, row]),
    [rawOrder, getCellContent, getNativeCellContent]);
  const getOriginalIndex = useCallback((row: number) => rawOrder ? rawOrder[row] ?? -1 : getNativeIndex(row),
    [rawOrder, getNativeIndex]);

  const onItemHovered = useCallback((args: GridMouseEventArgs) => {
    const row = args.location[1];
    _setHoverRow(args.kind !== "cell" ? undefined : row);
  }, []);

  const onHeaderMenuClick = useCallback((col: number) => {
    const column = cols[col];
    if (!column || _colsMap[col]?.sortable === false) return;
    const id = columnKey(column);
    const next = (prev: ColumnSort | undefined): ColumnSort => {
      if (prev && prev.id === id) {
        return {
          id,
          direction: prev.direction === "desc" ? "asc" : "desc"
        };
      } else {
        return {
          id,
          direction: "desc"
        };
      }
    };
    if (columnSort) columnSort.onChange(next(columnSort.value));
    else _setSort(next);
  }, [cols, _colsMap, columnSort]);

  return {
    ...sortArgs,
    getCellContent: sortedCellContent,
    getOriginalIndex,
    onColumnResize: onColumnResize,
    onItemHovered: onItemHovered,
    onHeaderMenuClick: disableSorting ? undefined : onHeaderMenuClick,
    columns: cols,
    rows: data.length,
    hoverRow: _hoverRow,
    sort: activeSort,
  };
}

export type UseAsyncDataGridProps<T> = {
  colsMap: GridColumnWithContent<T>[]
  pageSize: number,
  maxConcurrency: number,
  maxRows?: number
  getRowData: RowCallback<T>,
  onEdited: RowEditedCallback<T>,
  data: T[]
}

export type UseAsyncDataGridResult<T> =
  Pick<DataEditorProps, "onColumnResize" | "onItemHovered" | "columns" | "onHeaderMenuClick" | "rows">
  &
  ReturnType<typeof useColumnSort>
  &
  ReturnType<typeof useAsyncDataSource>
  & {
  // extend props here
  hoverRow?: number
}

export function useAsyncDataGrid<T>(props: UseAsyncDataGridProps<T>): UseAsyncDataGridResult<T> {
  const {
    colsMap,
    pageSize,
    maxConcurrency,
    maxRows,
    getRowData,
    onEdited,
    data,
  } = props;

  const ref = React.useRef<DataEditorRef | null>(null);

  // set state
  const [_hoverRow, _setHoverRow] = useState<number | undefined>(undefined);
  const [_sort, _setSort] = useState<GridSort>();
  const [_colsMap, _setColsMap] = useState(() => colsMap);

  const onColumnResize = useCallback((column: GridColumn, newSize: number) => {
    _setColsMap(prevColsMap => {
      const index = prevColsMap.findIndex(ci => ci.title === column.title);
      const newArray = [...prevColsMap];
      newArray.splice(index, 1, {
        ...prevColsMap[index],
        width: newSize
      });
      return newArray;
    });
  }, []);

  const cols = useMemo(() => {
    return _colsMap.map(getGridColumn);
  }, [_colsMap]);

  const colsMapRef = useRef(_colsMap);
  colsMapRef.current = _colsMap;

  const toCell = useCallback((row: T, col: number) => {
    return colsMapRef.current[col].getContent(row, col);
  }, [])

  const dataGridArgs = useAsyncDataSource<T>(
    pageSize,
    maxConcurrency,
    getRowData,
    toCell,
    onEdited,
    ref
  )

  const sortArgs = useDataGridColumnSort({
    columns: cols,
    getCellContent: dataGridArgs.getCellContent,
    rows: data.length,
    sort: _sort === undefined ? undefined : {
      column: cols[_sort.col],
      direction: _sort.direction,
      mode: "smart"
    }
  });

  const onItemHovered = useCallback((args: GridMouseEventArgs) => {
    const row = args.location[1];
    _setHoverRow(args.kind !== "cell" ? undefined : row);
  }, []);

  const onHeaderMenuClick = useCallback((col: number) => {
    _setSort((prev) => {
      if (prev && prev.col === col) {
        return {
          col: col,
          direction: prev.direction === "desc" ? "asc" : "desc"
        };
      } else {
        return {
          col: col,
          direction: "desc"
        };
      }
    });
  }, []);

  return {
    ...dataGridArgs,
    ...sortArgs,
    onColumnResize: onColumnResize,
    onItemHovered: onItemHovered,
    onHeaderMenuClick: onHeaderMenuClick,
    columns: cols,
    rows: maxRows ?? 10_000,
    hoverRow: _hoverRow,
  };
}
